import { Link } from 'react-router-dom';
import { MapPinIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '../ui/Card';
import { cn } from '../../utils/cn';
import { sanitizeHtml } from '../../utils/sanitize';
import type { Activity as APIActivity, ProtectedPerson } from '../../services/executive-protection.service';

// Extended Activity interface for component use
export interface Activity extends Omit<APIActivity, 'protectedPersonId' | 'protectedPersonName'> {
  personId: string;
  personName: string;
  protectedPersonId?: string;
  protectedPersonName?: string;
  protectedPersons?: ProtectedPerson[];
  riskAssessment?: 'low' | 'medium' | 'high' | string;
  protectionLevel?: 'standard' | 'enhanced' | 'high';
  notes?: string;
}

export interface ActivityCardProps {
  activity: Activity;
}

export function ActivityCard({ activity }: ActivityCardProps) {
  const statusColors: Record<string, string> = {
    scheduled: 'bg-primary-500 text-white dark:bg-primary-600 dark:text-white',
    planned: 'bg-primary-500 text-white dark:bg-primary-600 dark:text-white',
    PLANNED: 'bg-primary-500 text-white dark:bg-primary-600 dark:text-white',
    in_progress: 'bg-warning-500 text-white dark:bg-warning-600 dark:text-white',
    IN_PROGRESS: 'bg-warning-500 text-white dark:bg-warning-600 dark:text-white',
    completed: 'bg-success-500 text-white dark:bg-success-600 dark:text-white',
    COMPLETED: 'bg-success-500 text-white dark:bg-success-600 dark:text-white',
    cancelled: 'bg-secondary-400 text-white dark:bg-secondary-600 dark:text-white',
    CANCELLED: 'bg-secondary-400 text-white dark:bg-secondary-600 dark:text-white',
  };

  const protectionColors: Record<string, string> = {
    standard: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300',
    enhanced: 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-300',
    high: 'bg-danger-100 text-danger-700 dark:bg-danger-900/20 dark:text-danger-300',
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isSameDay = (a: string, b: string) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA.getFullYear() === dateB.getFullYear() &&
           dateA.getMonth() === dateB.getMonth() &&
           dateA.getDate() === dateB.getDate();
  };

  // Get protected persons count
  const protectedPersonsCount = activity.protectedPersons?.length ?? (activity.personName ? 1 : 0);
  const displayName = activity.protectedPersons?.[0]
    ? `${activity.protectedPersons[0].firstName} ${activity.protectedPersons[0].lastName}`
    : activity.personName;

  return (
    <Link to={`/security/executive-protection/activities/${activity.id}`} className="block">
      <Card hover="default" className="transition-all duration-200 hover:shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-medium text-secondary-900 dark:text-white">
                {activity.title}
              </h4>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {displayName}
                {protectedPersonsCount > 1 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 rounded">
                    +{protectedPersonsCount - 1} more
                  </span>
                )}
              </p>
            </div>
            {activity.status && (
              <span className={cn('px-2 py-1 text-xs font-medium rounded-full', statusColors[activity.status] ?? statusColors.planned)}>
                {activity.status.replace('_', ' ').toUpperCase()}
              </span>
            )}
          </div>

          <div className="space-y-2 mb-3">
            {activity.location && (
              <div className="flex items-center space-x-2 text-sm">
                <MapPinIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
                <span className="text-secondary-700 dark:text-secondary-300">
                  {activity.location}
                </span>
              </div>
            )}
            {(activity.startTime ?? activity.startDatetime) && (activity.endTime ?? activity.endDatetime) && (
              <div className="flex items-center space-x-2 text-sm">
                <ClockIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
                <span className="text-secondary-700 dark:text-secondary-300">
                  {formatDateTime((activity.startTime ?? activity.startDatetime) as string)} -{' '}
                  {isSameDay(
                    (activity.startTime ?? activity.startDatetime) as string,
                    (activity.endTime ?? activity.endDatetime) as string
                  )
                    ? formatTime((activity.endTime ?? activity.endDatetime) as string)
                    : formatDateTime((activity.endTime ?? activity.endDatetime) as string)}
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {activity.description && (
              <div
                className="text-xs text-secondary-600 dark:text-secondary-400 prose prose-sm dark:prose-invert max-w-none [&_p]:m-0 line-clamp-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(activity.description) }}
              />
            )}
            {activity.protectionLevel && (
              <span className={cn('px-2 py-1 text-xs font-medium rounded-full', protectionColors[activity.protectionLevel])}>
                {activity.protectionLevel.toUpperCase()} PROTECTION
              </span>
            )}
          </div>

          {activity.notes && (
            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-3 italic">
              {activity.notes}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export default ActivityCard;
